import { ACCESS_TOKEN_NAME } from 'constants/access-token';
import { AxiosInstance } from 'axios';
import { ApiResult, Result } from 'services/api/result.type';

import { mutateProfile } from 'hooks';
import { BaseApi } from 'services/api/base-api';
import {
  CompleteSignUpRequest,
  CompleteSignupResponseDto,
  ConfirmAffiliationDetailsRequestDto,
  ConfirmDiversityDetailsRequestDto,
  ConfirmFocusDetailsRequestDto,
  ConfirmIdentificationDetailsRequestDto,
  ConfirmJourneyDetailsRequestDto,
  ConfirmPersonalDetailsRequestDto,
  ConfirmResidencyDetailsRequestDto,
  ConfirmRoleDetailsRequestDto,
  GetSignUpStatusResponse,
  LogInRequest,
  LogInResponse,
  SignUpRequest,
} from './dto';
import { ConfirmActivationLinkResponseDto } from './dto/confirm-activation-link.response.dto';
export class AuthApi extends BaseApi {
  public constructor (protected readonly api: AxiosInstance) {
    super();

    this.logIn = this.logIn.bind(this);
    this.logOut = this.logOut.bind(this);
    this.masterLogIn = this.masterLogIn.bind(this);
    this.startSignUp = this.startSignUp.bind(this);
    this.completeSignUp = this.completeSignUp.bind(this);
    this.confirmPersonalDetails = this.confirmPersonalDetails.bind(this);
    this.resendActivationLink = this.resendActivationLink.bind(this);
    this.requestPasswordReset = this.requestPasswordReset.bind(this);
    this.resetPassword = this.resetPassword.bind(this);
  }

  public async logIn (reqData: LogInRequest): ApiResult<LogInResponse> {
    const result = await this.post<LogInResponse>('/auth/login', reqData, {
      withCredentials: true,
      baseURL: process.env.NEST_PUBLIC_AUTH_API_URL,
    });

    this.setTokenIfSuccessful(result);

    return result;
  }

  public async startSignUp (reqData: SignUpRequest): ApiResult<void> {
    this.removeToken();
    return await this.post('/auth/signup', reqData);
  }

  public async getSignUpStatus (): ApiResult<GetSignUpStatusResponse> {
    return await this.get('/auth/signup/status');
  }

  public async completeSignUp (
    data: CompleteSignUpRequest,
  ): ApiResult<CompleteSignupResponseDto> {
    this.removeToken();

    const result = await this.post<CompleteSignupResponseDto>(
      '/auth/signup/complete',
      data,
      {
        withCredentials: true,
        baseURL: process.env.NEST_PUBLIC_AUTH_API_URL,
      },
    );

    this.setTokenIfSuccessful(result);

    return result;
  }

  public async confirmPersonalDetails (
    data: ConfirmPersonalDetailsRequestDto,
  ): ApiResult<void> {
    return await this.post('/auth/signup/confirmPersonalDetails', data);
  }

  public async confirmIdentificationDetails (
    data: ConfirmIdentificationDetailsRequestDto,
  ): ApiResult<void> {
    return await this.post('/auth/signup/confirmIdentificationDetails', data);
  }

  public async confirmDiversityDetails (
    data: ConfirmDiversityDetailsRequestDto,
  ): ApiResult<void> {
    return await this.post('/auth/signup/confirmDiversityDetails', data);
  }

  public async confirmAffiliationDetails (
    data: ConfirmAffiliationDetailsRequestDto,
  ): ApiResult<void> {
    return await this.post('/auth/signup/confirmAffiliationDetails', data);
  }

  public async confirmJourneyDetails (
    data: ConfirmJourneyDetailsRequestDto,
  ): ApiResult<void> {
    return await this.post('/auth/signup/confirmJourneyDetails', data);
  }

  public async confirmResidencyDetails (
    data: ConfirmResidencyDetailsRequestDto,
  ): ApiResult<void> {
    return await this.post('/auth/signup/confirmResidencyDetails', data);
  }

  public async confirmFocusDetails (
    data: ConfirmFocusDetailsRequestDto,
  ): ApiResult<void> {
    return await this.post('/auth/signup/confirmFocusDetails', data);
  }

  public async confirmRoleDetails (
    data: ConfirmRoleDetailsRequestDto,
  ): ApiResult<void> {
    return await this.post('/auth/signup/confirmRoleDetails', data);
  }

  public async resendActivationLink (schoolEmail: string): ApiResult<void> {
    return await this.post('/auth/signup/resend', { schoolEmail });
  }

  public async requestPasswordReset (email: string): ApiResult<void> {
    this.removeToken();
    return await this.post('/auth/reset-password', { email });
  }

  public async resetPassword (token: string, password: string): ApiResult<void> {
    this.removeToken();
    return await this.post('/auth/reset-password', { token, password });
  }

  public async confirmActivationLink (
    token: string,
  ): ApiResult<ConfirmActivationLinkResponseDto> {
    return await this.post<ConfirmActivationLinkResponseDto>(
      '/auth/signup/confirm',
      { token },
    );
  }

  public async masterLogIn (): ApiResult<LogInResponse> {
    const result = await this.get<LogInResponse>('/auth/master', {
      withCredentials: true,
      baseURL: process.env.NEST_PUBLIC_AUTH_API_URL,
    });

    this.setTokenIfSuccessful(result);

    return result;
  }

  public async samlLogin (): Promise<void> {
    const result = await this.get<{ accessToken: string }>('/auth/token', {
      withCredentials: true,
      baseURL: process.env.NEST_PUBLIC_AUTH_API_URL,
    });

    this.setTokenIfSuccessful(result);
  }

  public async logOut (): Promise<void> {
    this.removeToken();
    await mutateProfile();
  }

  public async deleteAccount (): ApiResult<void> {
    return await this.delete<void>('/auth/delete-account');
  }

  private setTokenIfSuccessful (
    result: Result<LogInResponse | CompleteSignupResponseDto>,
  ): void {
    if (result.success) {
      const accessToken = result.value.accessToken;
      window.localStorage.setItem(ACCESS_TOKEN_NAME, accessToken);
      this.api.defaults.headers.common.Authorization = `Bearer: ${accessToken}`;
      void mutateProfile();
    }
  }

  private removeToken (): void {
    delete this.api.defaults.headers.common.Authorization;
    window.localStorage.removeItem(ACCESS_TOKEN_NAME);
    void mutateProfile();
  }
}
